import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

type Status = 'idle' | 'sending' | 'success' | 'error';

export default function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value.toString()));

    setStatus('sending');
    try {
      const response = await fetch('https://readdy.ai/api/form/d70ldjmth28qd804ct70', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (response.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1 pt-20">
        <section className="py-16 bg-white border-t-4 border-[#1A2B4A]">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#1A2B4A] mb-4">
              Nous écrire
            </p>
            <h1 className="font-poppins text-4xl font-semibold text-[#1A2B4A] mb-4">
              Contactez-nous
            </h1>
            <p className="text-gray-500 text-sm leading-relaxed">
              Pour toute demande de conseil ou d'information, n'hésitez pas à nous contacter.
              Nous vous répondrons dans les meilleurs délais.
            </p>
          </div>
        </section>

        <section className="py-16 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
              <div className="lg:col-span-2 bg-[#1A2B4A] rounded-lg p-10 text-white">
                <h2 className="font-poppins text-xl font-semibold mb-8">
                  Informations
                </h2>
                <ul className="flex flex-col gap-7">
                  <li className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-md flex-shrink-0">
                      <i className="ri-building-line text-base"></i>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs mb-1">Entreprise</p>
                      <p className="text-sm font-medium">SOMINKA</p>
                      <p className="text-gray-300 text-xs mt-0.5">Amelia Anna Sominka</p>
                      <p className="text-gray-400 text-xs mt-0.5">SIRET : 101 634 145 00011</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-md flex-shrink-0">
                      <i className="ri-map-pin-line text-base"></i>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs mb-1">Adresse</p>
                      <p className="text-sm leading-relaxed">
                        4 Rue Judaïque<br />33000 Bordeaux<br />France
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-md flex-shrink-0">
                      <i className="ri-phone-line text-base"></i>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs mb-1">Téléphone</p>
                      <a
                        href="tel:+33682533668"
                        className="text-sm hover:text-gray-200 transition-colors"
                      >
                        +33 6 82 53 36 68
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-md flex-shrink-0">
                      <i className="ri-mail-line text-base"></i>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs mb-1">Email</p>
                      <a
                        href="mailto:ameliasominkaaa@outlook.com"
                        className="text-sm hover:text-gray-200 transition-colors break-all"
                      >
                        ameliasominkaaa@outlook.com
                      </a>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="lg:col-span-3 bg-white rounded-lg p-10">
                <h2 className="font-poppins text-xl font-semibold text-[#1A2B4A] mb-8">
                  Votre demande
                </h2>

                {status === 'success' ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-50 text-green-600 mb-4">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <h3 className="font-poppins text-lg font-medium text-gray-800 mb-2">
                      Message envoyé !
                    </h3>
                    <p className="text-gray-500 text-sm">
                      Nous reviendrons vers vous dans les meilleurs délais.
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    id="contact-sominka"
                    className="flex flex-col gap-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">
                          Nom *
                        </label>
                        <input
                          type="text"
                          name="nom"
                          required
                          placeholder="Votre nom"
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-[#1A2B4A] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">
                          Téléphone
                        </label>
                        <input
                          type="tel"
                          name="telephone"
                          placeholder="Votre téléphone"
                          className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-[#1A2B4A] transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="votre@email.com"
                        className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-[#1A2B4A] transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">
                        Description de la demande *{' '}
                        <span className="text-gray-400 font-normal">
                          ({charCount}/500)
                        </span>
                      </label>
                      <textarea
                        name="description"
                        required
                        rows={5}
                        maxLength={500}
                        onChange={(e) => setCharCount(e.target.value.length)}
                        placeholder="Décrivez votre besoin en détail..."
                        className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-[#1A2B4A] transition-colors resize-none"
                      />
                    </div>

                    {status === 'error' && (
                      <p className="text-red-500 text-xs">
                        Une erreur s'est produite. Veuillez réessayer ou nous contacter directement.
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'sending'}
                      className="w-full py-3 rounded-md bg-[#1A2B4A] text-white text-sm font-medium hover:bg-[#243a5e] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                    >
                      {status === 'sending' ? 'Envoi en cours...' : 'Envoyer'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="rounded-lg overflow-hidden h-80">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2829.543!2d-0.5786!3d44.8421!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd5527e8f751ca81%3A0x796386037b397a89!2s4%20Rue%20Juda%C3%AFque%2C%2033000%20Bordeaux%2C%20France!5e0!3m2!1sfr!2sfr!4v1710000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="SOMINKA – 4 Rue Judaïque, 33000 Bordeaux"
              ></iframe>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
